import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from "../contexts/ThemeContext";
import { THEMES, THEME_COLORS } from "../components/Constants";
import userService from "../services/userService";
import { MdSettingsInputAntenna, MdColorLens, MdLock, MdAddLink, MdGroup, MdDeleteForever, MdArrowBack, MdEdit, MdClose, MdRefresh, MdOutlineMailOutline, MdOutlineReadMore, MdOutlineClear } from 'react-icons/md'; 
import { useNotification } from '../contexts/NotificationContext';
import { authFetch } from '../utils/api';

import accountService from "../services/accountService"; 
import personService from "../services/personService"; 
import PasswordChangeModal from '../components/PasswordChangeModal';

// ⭐ 1. IMPORTS DES IMAGES DE FOND (utilisées pour la galerie)
import BackgroundImageBlue from '../images/background_blue.png';
import BackgroundImageGreen from '../images/background_green.png';
import BackgroundImageRed from '../images/background_red.png';
import BackgroundImageOrange from '../images/background_orange.png';
import BackgroundImageYellow from '../images/background_yellow.png';

const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

// ⭐ 2. STRUCTURE DE DONNÉES POUR LA GALERIE DE THÈMES
const THEME_GALLERY = [
    { 
        value: THEMES.BLUE, 
        label: "Bleu Aqua (Défaut)", 
        image: BackgroundImageBlue,
        description: "Contraste élevé et couleurs rafraîchissantes."
    },
    { 
        value: THEMES.GREEN, 
        label: "Vert Doux", 
        image: BackgroundImageGreen,
        description: "Apaisant et facile pour les yeux."
    },
    { 
        value: THEMES.RED, 
        label: "Bordeaux Grenat", 
        image: BackgroundImageRed,
        description: "Énergique avec des accents profonds."
    },
    { 
        value: THEMES.ORANGE, 
        label: "Orange Aube",  
        image: BackgroundImageOrange,
        description: "Chaleureux et vibrant pour les journées lumineuses."
    },
    { 
        value: THEMES.YELLOW, 
        label: "Jaune Clarté", 
        image: BackgroundImageYellow,
        description: "Lumineux et minimaliste."
    },
];

const ROLES_OPTIONS = [
    { value: 'Admin', label: 'Administrateur', access: 'read_write' },
    { value: 'Editor', label: 'Éditeur', access: 'read_write' },
    { value: 'Viewer', label: 'Lecteur', access: 'read_only' }
];

/**
 * Construit la structure arborescente (Personne -> Comptes) à partir des listes plates.
 * @param {Array} persons - Liste des objets personne.
 * @param {Array} accounts - Liste des objets compte, incluant personId.
 * @returns {Array} Structure arborescente des ressources.
 */
const buildResourcesTree = (persons, accounts) => {
    // Crée un map rapide des comptes par personId
    const accountsByPerson = accounts.reduce((acc, account) => {
        const personId = account.personId || account.person_id; // Assurez la compatibilité des clés
        if (!acc[personId]) {
            acc[personId] = [];
        }
        acc[personId].push(account);
        return acc;
    }, {});

    // Construit l'arbre final, en filtrant les personnes qui n'appartiennent pas au groupe principal
    // (pour l'instant, on ne permet de partager que les ressources du groupe principal)
    return persons.filter(p => p.isOwnedByGroup).map(person => ({ // Filtre CRITIQUE
        ...person,
        accounts: accountsByPerson[person.personId || person.id] || [],
    }));
};

// ===============================================
// ⚛️ SOUS-COMPOSANTS DES ONGLETS
// ===============================================

// 1. Gestion des données utilisateur (Mon Compte)
const UserDataSettings = ({ currentUser, themeColors, notify, updateCurrentUser }) => {
    const [name, setName] = useState(currentUser.name);
    const [email, setEmail] = useState(currentUser.email);
    const [showPwModal, setShowPwModal] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    
    // --- NOUVEAU : Logique Support ---
    const [supportTimeLeft, setSupportTimeLeft] = useState(null);

    const handleEnableSupport = async () => {
        try {
            const res = await authFetch.post('/api/user/enable-support', { minutes: 15 });
            const data = await res.json();
            notify("Accès support activé pour 15 minutes.", "success");
            updateCurrentUser({ support_access_expires: data.expiresAt });
        } catch (err) { notify("Erreur activation support", "error"); }
    };

     const handleUpdateName = async (e) => {
        e.preventDefault();
        if (!name.trim()) return notify("Le nom ne peut pas être vide.", "warning"); 
            setIsUpdating(true);
        try {
            const response = await authFetch.put('/api/user/update-name', { name });
            if (response.ok) {
                updateCurrentUser({ name: name });
                notify("Nom mis à jour avec succès !", "success");
            } else {
                const data = await response.json();
                throw new Error(data.error || "Erreur lors de la mise à jour.");
            }
        } catch (err) {
            notify(err.message, "error");
        } finally { 
            setIsUpdating(false); 
        }
    }; 

    const handleRequestEmailChange = async () => {
        if (email === currentUser.email) return;
        setIsUpdating(true);
        try {
            await authFetch.post('/api/user/request-email-change', { newEmail: email });
            notify("Email de confirmation envoyé à la nouvelle adresse.", "success");
        } catch (err) {
            notify(err.message || "Erreur lors de la demande de changement d'email.", "error");
        } finally { setIsUpdating(false); }
    };

    useEffect(() => {
        if (!currentUser.support_access_expires) return;
        
        const timer = setInterval(() => {
            const diff = new Date(currentUser.support_access_expires) - new Date();
            if (diff <= 0) {
                setSupportTimeLeft(null);
                clearInterval(timer);
            } else {
                const mins = Math.floor(diff / 60000);
                const secs = Math.floor((diff % 60000) / 1000);
                setSupportTimeLeft(`${mins}:${secs < 10 ? '0' : ''}${secs}`);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [currentUser.support_access_expires]);

    return (
        <div style={styles.settingsContent}>
            <h3 style={{ color: themeColors.text }}>Informations personnelles</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', marginTop: '20px' }}>
                {/* --- BLOC IDENTITÉ --- */}
                <div style={styles.contentArea(themeColors)}>
                    <form onSubmit={handleUpdateName} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div>
                            <label style={styles.label}>Votre Nom :</label>
                            <input 
                                style={{...styles.input(themeColors), width: '100%'}} 
                                type="text" 
                                value={name} 
                                onChange={(e) => setName(e.target.value)} 
                            />
                        </div>
                        <button 
                            style={styles.saveButton(themeColors)} 
                            type="submit" 
                            disabled={isUpdating || name === currentUser.name}
                        >
                            {isUpdating ? "Enregistrement..." : "Enregistrer mon nom"}
                        </button>
                    </form>

                    <hr style={{ margin: '25px 0', border: 'none', borderTop: `1px solid ${themeColors.inputBorder}` }} />

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div>
                            <label style={styles.label}>Adresse e-mail :</label>
                            <input 
                                style={{...styles.input(themeColors), width: '100%'}} 
                                type="email" 
                                value={email} 
                                onChange={(e) => setEmail(e.target.value)} 
                            />
                        </div>
                        <button 
                            style={{ ...styles.saveButton(themeColors), backgroundColor: themeColors.primary }} 
                            onClick={handleRequestEmailChange}
                            disabled={isUpdating || email === currentUser.email}
                        >
                            Modifier mon adresse email
                        </button>
                    </div>
                </div>

                {/* --- BLOC SÉCURITÉ --- */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Bloc Mot de passe existant */}
                    <div style={styles.securityCard(themeColors)}>
                        <MdLock size={30} style={{ color: themeColors.warning }} />
                        <h4>Sécurité du compte</h4>
                        <button onClick={() => setShowPwModal(true)} style={styles.saveButton(themeColors)}>Changer le mot de passe</button>
                    </div>

                    {/* 🛠️ NOUVEAU BLOC SUPPORT ON-DEMAND */}
                    <div style={{ ...styles.securityCard(themeColors), border: `2px solid ${supportTimeLeft ? themeColors.success : themeColors.inputBorder}` }}>
                        <MdSettingsInputAntenna size={30} style={{ color: supportTimeLeft ? themeColors.success : themeColors.textSecondary }} />
                        <h4>Assistance à distance</h4>
                        <p style={{ fontSize: '0.85em' }}>Ouvrez un accès temporaire pour permettre à un admin de vous aider.</p>
                        
                        {supportTimeLeft ? (
                            <div style={{ color: themeColors.success, fontWeight: 'bold', padding: '10px' }}>
                                🔓 Accès ouvert : {supportTimeLeft}
                            </div>
                        ) : (
                            <button onClick={handleEnableSupport} style={{ ...styles.saveButton(themeColors), backgroundColor: themeColors.primary }}>
                                Activer l'accès (15 min)
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {showPwModal && <PasswordChangeModal onClose={() => setShowPwModal(false)} themeColors={themeColors} notify={notify} />}
        </div>
    );
};

// 2. Gestion des utilisateurs secondaires (Gestion des Utilisateurs)
const UserManagement = ({ themeColors, notify, currentUser}) => { 
    const [users, setUsers] = useState([]);
    const [email, setEmail] = useState('');
    // ⭐ MISE À JOUR: Le rôle initial est 'Editor' mais on envoie l'accessLevel par défaut 'read_only' ou 'read_write' au Backend
    const [role, setRole] = useState('Editor'); 
    const [accessLevel, setAccessLevel] = useState('read_only');
    
    const [isLoading, setIsLoading] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // ⭐ NOUVEAU: État pour l'arbre des ressources
    const [resourcesTree, setResourcesTree] = useState([]);

    // ⭐ Renommage des états pour la sélection simplifiée (stocke les IDs pour le POST)
    const [selectedResourceIds, setSelectedResourceIds] = useState({
        persons: [], // Personnes partagées
        accounts: [], // Comptes partagés
    });
    const [isResourcesLoading, setIsResourcesLoading] = useState(false); 

    const isCurrentUserAdmin = currentUser?.role === 'Admin' || !currentUser.mainUserId;
    const hasToken = !!currentUser?.token;
    
    const groupId = currentUser.mainUserId || currentUser.id; 
    
    const currentUserInfo = users.find(u => u.id === currentUser?.id) || currentUser;

    const roles = ROLES_OPTIONS;

    // ⭐ 1. UTILISATION DE useCALLBACK POUR fetchUsers (inchangé)
    const fetchUsers = useCallback(async () => {
        if (!isCurrentUserAdmin || !hasToken) return; 
        setIsLoading(true);
        try {
            const tokenToUse = currentUser.token; 
            const data = await userService.getUsersList(tokenToUse);
            setUsers(data.users); 
        } catch (error) {
            console.error("Erreur de récupération des utilisateurs:", error);
            const errorMessage = error.response?.data?.error || error.message || 'Erreur lors de la récupération des utilisateurs.';
            notify(errorMessage, 'error');
            setUsers([]);
        } finally {
            setIsLoading(false);
        }
    }, [isCurrentUserAdmin, hasToken, notify, currentUser.token]);

    // ⭐ NOUVEAU: Fonction pour charger toutes les personnes et tous les comptes
    const fetchResources = useCallback(async () => {
        // Ajout d'une vérification pour groupId (devrait être toujours vrai si isCurrentUserAdmin est vrai)
        if (!isCurrentUserAdmin || !hasToken || !groupId) return;
        setIsResourcesLoading(true);
        try {
            // Utiliser le groupId calculé
            const [personData, accountData] = await Promise.all([
                personService.getAllPersons(groupId), // <-- Utilisation de groupId
                accountService.getAllAccounts(groupId) // <-- Utilisation de groupId
            ]); 

            // Construction de l'arbre (filtré sur les personnes du groupe principal)
            const tree = buildResourcesTree(personData || [], accountData || []);
            setResourcesTree(tree);

        } catch (error) {
            console.error("Erreur de récupération des ressources:", error);
            // Si le service n'est pas bien implémenté, cela peut causer une boucle de rendu.
            // La gestion des erreurs DOIT empêcher un re-rendu involontaire.
            notify("Erreur lors du chargement des personnes/comptes pour le partage.", 'error');
        } finally {
            setIsResourcesLoading(false);
        }
    // ⭐ MISE À JOUR: Utilisation de groupId dans les dépendances
    }, [isCurrentUserAdmin, hasToken, notify, groupId])

    // ⭐ Gestion des sélecteurs
    const togglePersonSelection = (person, checked) => {
        const personId = person.id;
        // Utilisez le person_id du compte car la clé est personId/person_id (pour les requêtes)
        const accountIds = person.accounts.map(a => a.id); 
        
        setSelectedResourceIds(prev => {
            let newPersons = prev.persons.filter(id => id !== personId);
            let newAccounts = prev.accounts.filter(id => !accountIds.includes(id));

            if (checked) {
                newPersons.push(personId);
                // Si on coche le parent, on coche tous les enfants (sans duplication)
                newAccounts = [...new Set([...prev.accounts, ...accountIds])]; 
            } else {
                // Si on décoche le parent, on retire tous les comptes enfants aussi.
                newAccounts = prev.accounts.filter(id => !accountIds.includes(id));
            }
            
            return { persons: newPersons, accounts: newAccounts };
        });
    };

    const toggleAccountSelection = (accountId, personId, checked) => {
        setSelectedResourceIds(prev => {
            let newAccounts = prev.accounts.filter(id => id !== accountId);
            let newPersons = prev.persons.filter(id => id !== personId); // Retire la personne si on modifie un compte (pour permettre l'état partiel)
            
            if (checked) {
                newAccounts.push(accountId);
            }

            // Vérifier si tous les comptes de la personne sont maintenant sélectionnés
            const person = resourcesTree.find(p => p.id === personId);
            if (person) {
                const allAccountIds = person.accounts.map(a => a.id);
                
                // IDs de comptes après l'ajout/retrait
                const currentlySelected = checked 
                    ? [...newAccounts, accountId]
                    : newAccounts;

                const allSelectedForPerson = allAccountIds.every(id => currentlySelected.includes(id));

                if (allSelectedForPerson) {
                    // Si tous sont cochés, cocher la personne
                    if (!newPersons.includes(personId)) {
                        newPersons.push(personId);
                    }
                } else if (newPersons.includes(personId)) {
                    // Si la personne était cochée et qu'au moins un compte est décoché, la décocher
                    newPersons = newPersons.filter(id => id !== personId);
                }
            }

            return { 
                persons: [...new Set(newPersons)], 
                accounts: [...new Set(newAccounts)] // Garantir l'unicité
            };
        });
    };
    
    const handleInvite = async (e) => {
        e.preventDefault();
        if (!email || !role) {
            return notify('Veuillez renseigner un email et un rôle.', 'warning');
        }
        
        // La vérification des ressources est déjà faite par le sélecteur, mais on garde une sécurité.
        if (selectedResourceIds.persons.length === 0 && selectedResourceIds.accounts.length === 0) {
             return notify('Veuillez sélectionner au moins une Personne ou un Compte à partager.', 'warning');
        }
        
        setIsSending(true);
        try {
            await userService.inviteUser(
                email, 
                role, 
                selectedResourceIds.persons, 
                selectedResourceIds.accounts,
                accessLevel // NOUVEAU: Envoi du niveau d'accès réel
            );
            notify(`Invitation envoyée à ${email} !`, 'success');
            
            // Réinitialisation après succès
            setEmail('');
            setRole('Editor');
            setAccessLevel('read_only');
            setSelectedResourceIds({ persons: [], accounts: [] });

            await fetchUsers(); 
        } catch (error) {
            const errorMessage = error.response?.data?.error || "Échec de l'envoi de l'invitation.";
            notify(errorMessage, 'error');
        } finally {
            setIsSending(false);
        }
    };
    
    const handleRemove = async (targetUserId, targetUserName) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir retirer **${targetUserName}** du compte ? Ses données créées lui resteront, mais il perdra l'accès aux vôtres.`)) {
            return;
        }

        try {
            await userService.removeGroupUser(targetUserId);
            notify(`${targetUserName} a été retiré du compte.`, 'success');
            await fetchUsers();
        } catch (error) {
            const errorMessage = error.response?.data?.error || "Échec de la suppression de l'utilisateur.";
            notify(errorMessage, 'error');
        }
    };
    
    useEffect(() => {
        let isMounted = true;

        if (isCurrentUserAdmin && hasToken) {
            const loadInitialData = async () => {
                try {
                    await fetchUsers();
                    if (isMounted) await fetchResources();
                } catch (err) {
                    console.error("Échec du chargement initial");
                }
            };
            
            loadInitialData();
        }

        return () => { isMounted = false; };
    }, [isCurrentUserAdmin, hasToken, fetchUsers, fetchResources]);

    return (
        <div style={styles.settingsContent}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ color: themeColors.text }}>Gestion des accès du compte</h3>
                {isCurrentUserAdmin && (
                    <button 
                        onClick={() => { fetchUsers(); fetchResources(); }} 
                        style={{ ...styles.saveButton(themeColors), backgroundColor: themeColors.defaultButton, color: themeColors.textSecondary }}
                        title="Actualiser la liste"
                        disabled={isLoading || isResourcesLoading}
                    >
                        <MdRefresh size={20} style={{ marginRight: '5px' }} />
                        {(isLoading || isResourcesLoading) ? 'Chargement...' : 'Actualiser'}
                    </button>
                )}
            </div>
            <p style={{ color: themeColors.text }}>
                En tant que **{currentUser?.role || 'Utilisateur'}**, vous gérez les accès aux données du compte.
            </p>
            
            {!isCurrentUserAdmin && (
                <div style={{ ...styles.contentArea(themeColors), padding: '20px', textAlign: 'center' }}>
                    <p style={{ color: themeColors.textSecondary }}>Vous n'avez pas les droits d'administration pour gérer les utilisateurs de ce compte.</p>
                </div>
            )}
            
            {isCurrentUserAdmin && (
                <>
                    {/* Formulaire d'Invitation ÉTENDU */}
                    <div style={{ ...styles.contentArea(themeColors), marginBottom: '20px' }}>
                        <h4 style={{ color: themeColors.primary, marginTop: 0 }}>Inviter un nouvel utilisateur et définir les permissions</h4>
                        
                        {isResourcesLoading ? (
                            <p style={{ color: themeColors.text }}>Chargement des ressources...</p>
                        ) : (
                            <form onSubmit={handleInvite}>
                                
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '15px' }}>
                                    {/* 1. Email et Rôle */}
                                    <div style={{ flexGrow: 1 }}>
                                        <label style={styles.label}>Adresse e-mail :</label>
                                        <input 
                                            style={{ ...styles.input(themeColors), width: '100%', marginTop: '5px' }} 
                                            type="email" 
                                            value={email} 
                                            onChange={(e) => setEmail(e.target.value)} 
                                            placeholder="nom@exemple.com"
                                            required
                                        />
                                    </div>
                                    <div style={{ width: '150px' }}>
                                        <label style={styles.label}>Rôle pour le groupe :</label>
                                        <select 
                                            style={{ ...styles.input(themeColors), width: '100%', marginTop: '5px' }}
                                            value={role}
                                            onChange={(e) => setRole(e.target.value)}
                                            required
                                        >
                                            {roles.map(r => (
                                                <option key={r.value} value={r.value}>{r.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                     {/* NOUVEAU: Sélecteur du niveau d'accès pour les ressources partagées */}
                                    <div style={{ width: '180px' }}>
                                        <label style={styles.label}>Niveau d'Accès :</label>
                                        <select 
                                            style={{ ...styles.input(themeColors), width: '100%', marginTop: '5px' }}
                                            value={accessLevel}
                                            onChange={(e) => setAccessLevel(e.target.value)}
                                            required
                                        >
                                            <option value="read_only">Lecture Seule</option>
                                            <option value="read_write">Lecture & Écriture</option>
                                        </select>
                                    </div>
                                    
                                </div>

                                {/* 2. Sélecteurs de Ressources (Utilisation du nouveau composant) */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={styles.label}>Sélectionner les ressources à Partager <span style={{ color: themeColors.error }}>*</span></label>
                                    <ResourceTreeSelector
                                        themeColors={themeColors}
                                        resourcesTree={resourcesTree}
                                        selectedResourceIds={selectedResourceIds}
                                        togglePersonSelection={togglePersonSelection}
                                        toggleAccountSelection={toggleAccountSelection}
                                    />
                                </div>

                                {/* 3. Bouton d'Envoi */}
                                <div style={{ textAlign: 'right', marginTop: '15px' }}>
                                    <button 
                                        style={{ ...styles.saveButton(themeColors), backgroundColor: themeColors.primary, height: '40px' }} 
                                        type="submit"
                                        disabled={isSending || (selectedResourceIds.persons.length === 0 && selectedResourceIds.accounts.length === 0)}
                                    >
                                        <MdOutlineMailOutline size={20} style={{ marginRight: '5px' }} />
                                        {isSending ? 'Envoi...' : 'Envoyer l\'Invitation'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Liste des Utilisateurs */}
                    <h4 style={{ color: themeColors.text }}>Utilisateurs du Compte ({users.length})</h4>
                    <div style={styles.userListContainer(themeColors)}>
                        {users.map(user => (
                            <UserRow 
                                key={user.id} 
                                user={user} 
                                themeColors={themeColors} 
                                isCurrentUserAdmin={isCurrentUserAdmin}
                                isSelf={user.id === currentUserInfo?.id}
                                roles={roles}
                                notify={notify}
                                onUpdate={fetchUsers}
                                onRemove={handleRemove}
                                fetchResources={fetchResources} // Pour rafraîchir la liste de partage
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

// ⭐ NOUVEAU SOUS-COMPOSANT : ResourceTreeSelector (pour la sélection des ressources)
const ResourceTreeSelector = ({ themeColors, resourcesTree, selectedResourceIds, togglePersonSelection, toggleAccountSelection }) => {
    
    // Fonction utilitaire pour obtenir l'état de la personne (coché/partiel/non coché)
    const getPersonState = (person) => {
        const personId = person.id;
        
        // Vrai si la personne est cochée dans l'état principal
        const isPersonCheckedExplicitly = selectedResourceIds.persons.includes(personId);
        
        const allAccountIds = person.accounts.map(a => a.id);
        if (allAccountIds.length === 0) {
             return isPersonCheckedExplicitly ? 'checked' : 'unchecked';
        }

        const selectedAccountsCount = allAccountIds.filter(id => selectedResourceIds.accounts.includes(id)).length;
        
        if (selectedAccountsCount === 0 && !isPersonCheckedExplicitly) {
            return 'unchecked';
        }
        
        // Si la personne est explicitement cochée, ou si tous les comptes sont sélectionnés
        if (isPersonCheckedExplicitly || selectedAccountsCount === allAccountIds.length) {
            return 'checked';
        }

        // Si certains comptes sont sélectionnés, mais pas tous
        if (selectedAccountsCount > 0) {
            return 'partial';
        }
        
        return 'unchecked';
    };
    
    // Gestionnaire de changement pour la personne (Parent)
    const handlePersonChange = (person, currentCheckedState) => {
        const shouldCheck = !currentCheckedState;
        togglePersonSelection(person, shouldCheck);
    };

    return (
        <div style={styles.treeSelectorContainer(themeColors)}>
            {resourcesTree.length === 0 ? (
                 <p style={{ color: themeColors.textSecondary, textAlign: 'center', margin: 0 }}>
                    Aucune personne gérée n'a été trouvée pour le partage.
                 </p>
            ) : (
                resourcesTree.map(person => {
                    const personState = getPersonState(person);
                    const isChecked = personState === 'checked';
                    const isPartial = personState === 'partial';

                    return (
                        <div key={person.id} style={styles.treePersonRow(themeColors)}>
                            {/* Ligne Parent : La Personne */}
                            <div style={styles.treeRowHeader}>
                                <input 
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handlePersonChange(person, isChecked || isPartial)}
                                    ref={el => {
                                        if (el) el.indeterminate = isPartial;
                                    }}
                                />
                                <span style={{ fontWeight: 'bold' }}>{person.name}</span>
                                <span style={styles.badge}>{person.accounts.length} comptes</span>
                            </div>

                            {/* Lignes Enfants : Les Comptes */}
                            {person.accounts.length > 0 && (
                                <div style={styles.treeChildren}>
                                    {person.accounts.map(account => (
                                        <div key={account.id} style={styles.treeAccountRow}>
                                            <input 
                                                type="checkbox"
                                                checked={selectedResourceIds.accounts.includes(account.id)}
                                                onChange={(e) => toggleAccountSelection(account.id, person.id, e.target.checked)}
                                            />
                                            <span>{account.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })
            )}
        </div>
    );
};


// ⭐ NOUVEAU SOUS-COMPOSANT : Ligne d'Utilisateur
const UserRow = ({ user, themeColors, isCurrentUserAdmin, isSelf, roles, notify, onUpdate, onRemove, fetchResources }) => {
    const isPending = user.invitation_token && !user.is_verified;
    const isMainUser = !user.main_user_id;

    // État d'édition pour le rôle
    const [isEditingRole, setIsEditingRole] = useState(false);
    const [newRole, setNewRole] = useState(user.role);
    
    // État d'édition pour le nom
    const [isEditingName, setIsEditingName] = useState(false);
    const [newName, setNewName] = useState(user.name);

    // ⭐ NOUVEAU: États pour la gestion des permissions existantes
    const [isManagingPermissions, setIsManagingPermissions] = useState(false);
    const [permissionLoading, setPermissionLoading] = useState(false);
    
    // ⭐ États de travail pour la modification des permissions
    const [isEditingPermissions, setIsEditingPermissions] = useState(false);
    const [resourcesTree, setResourcesTree] = useState([]);
    const [selectedResourceIds, setSelectedResourceIds] = useState({
        persons: [],
        accounts: [],
    });
    const [accessLevel, setAccessLevel] = useState('read_only');

    const sharerGroupId = user.main_user_id || user.id;

    // ⭐ Fonction pour charger les permissions existantes et toutes les ressources
    const fetchUserPermissionsAndResources = useCallback(async () => {
         if (!isCurrentUserAdmin || isMainUser || isPending) return;
         setPermissionLoading(true);
         try {
            // 1. Récupérer toutes les ressources (Personnes et Comptes du groupe principal)
            const [personData, accountData] = await Promise.all([
                personService.getAllPersons(sharerGroupId), // <-- Utilisation de sharerGroupId
                accountService.getAllAccounts(sharerGroupId) // <-- Utilisation de sharerGroupId
            ]);
             const tree = buildResourcesTree(personData || [], accountData || []);
             setResourcesTree(tree);
             
             // 2. Récupérer les permissions spécifiques à cet utilisateur (Backend à créer)
             // Simule la récupération: on pourrait implémenter une route '/permissions/:userId'
             // const permissions = await userService.getPermissions(user.id);

             // Pour le moment, on simule l'état initial des permissions (pour les mettre à jour)
             const dummyPermissions = {
                 persons: [
                      // Simuler quelques personnes partagées (pour l'exemple)
                 ],
                 accounts: [
                      // Simuler quelques comptes partagés (pour l'exemple)
                 ]
             }
             
             // Initialiser la sélection de l'arbre à partir des permissions existantes
             setSelectedResourceIds({
                 persons: dummyPermissions.persons.map(p => p.person_id),
                 accounts: dummyPermissions.accounts.map(a => a.account_id)
             });


         } catch (error) {
             console.error("Erreur de récupération des permissions:", error);
             notify("Erreur lors du chargement des permissions de cet utilisateur.", 'error');
         } finally {
             setPermissionLoading(false);
         }
    }, [isCurrentUserAdmin, isMainUser, isPending, sharerGroupId, notify]);
    
    
    // ⭐ Handle pour l'édition de rôle
    const handleRoleUpdate = async () => {
        if (newRole === user.role) {
            return setIsEditingRole(false);
        }
        try {
            await userService.updateGroupUser(user.id, { role: newRole });
            notify(`Rôle de ${user.name} mis à jour à ${newRole}.`, 'success');
            onUpdate();
        } catch (error) {
            const errorMessage = error.response?.data?.error || "Échec de la mise à jour du rôle.";
            notify(errorMessage, 'error');
        } finally {
            setIsEditingRole(false);
        }
    };
    
    // ⭐ Handle pour l'édition de nom
    const handleNameUpdate = async () => {
        if (newName === user.name) {
            return setIsEditingName(false);
        }
        try {
            await userService.updateGroupUser(user.id, { name: newName });
            notify(`Nom de ${user.name} mis à jour.`, 'success');
            onUpdate();
        } catch (error) {
            const errorMessage = error.response?.data?.error || "Échec de la mise à jour du nom.";
            notify(errorMessage, 'error');
        } finally {
            setIsEditingName(false);
        }
    };
    
    // ⭐ Handle pour le partage de ressources
    const handleSavePermissions = async () => {
         setPermissionLoading(true);
         try {
             // Simuler l'envoi de la permission (À implémenter au Backend)
             // Vous devrez créer des routes pour: POST /permissions/person et POST /permissions/account
             // for (const personId of selectedResourceIds.persons) {
             //     await userService.setPersonPermission(personId, user.id, accessLevel, true);
             // }
             // for (const accountId of selectedResourceIds.accounts) {
             //     await userService.setAccountPermission(accountId, user.id, accessLevel, true);
             // }
             
             // Pour la demo, on simule une permission réussie
             notify(`Permissions mises à jour pour ${user.name} (Simulation).`, 'success');
             setIsEditingPermissions(false);
             fetchUserPermissionsAndResources(); // Recharger les permissions après la mise à jour
             fetchResources(); // Recharger les ressources du parent pour la mise à jour des noms

         } catch (error) {
             const errorMessage = error.response?.data?.error || "Échec de la mise à jour des permissions.";
             notify(errorMessage, 'error');
         } finally {
             setPermissionLoading(false);
         }
    };
    
    // ⭐ Fonctions de sélection de l'arbre pour les permissions (réutilisent les fonctions de UserManagement)
    const togglePersonSelectionForUpdate = (person, checked) => {
         const personId = person.id;
         const accountIds = person.accounts.map(a => a.id); 
         
         setSelectedResourceIds(prev => {
             let newPersons = prev.persons.filter(id => id !== personId);
             let newAccounts = prev.accounts.filter(id => !accountIds.includes(id));

             if (checked) {
                 newPersons.push(personId);
                 newAccounts = [...new Set([...prev.accounts, ...accountIds])]; 
             } else {
                 newAccounts = prev.accounts.filter(id => !accountIds.includes(id));
             }
             
             return { persons: newPersons, accounts: newAccounts };
         });
    };

    const toggleAccountSelectionForUpdate = (accountId, personId, checked) => {
         setSelectedResourceIds(prev => {
             let newAccounts = prev.accounts.filter(id => id !== accountId);
             let newPersons = prev.persons.filter(id => id !== personId);
             
             if (checked) {
                 newAccounts.push(accountId);
             }

             const person = resourcesTree.find(p => p.id === personId);
             if (person) {
                 const allAccountIds = person.accounts.map(a => a.id);
                 const currentlySelected = checked 
                     ? [...newAccounts, accountId]
                     : newAccounts;

                 const allSelectedForPerson = allAccountIds.every(id => currentlySelected.includes(id));

                 if (allSelectedForPerson) {
                     if (!newPersons.includes(personId)) {
                         newPersons.push(personId);
                     }
                 } else if (newPersons.includes(personId)) {
                     newPersons = newPersons.filter(id => id !== personId);
                 }
             }

             return { 
                 persons: [...new Set(newPersons)], 
                 accounts: [...new Set(newAccounts)]
             };
         });
    };


    return (
        // Ligne de l'utilisateur
        <div style={styles.userRow(themeColors, isMainUser)}>
            
            <div style={{ flex: 2, display: 'flex', flexDirection: 'column' }}>
                {isEditingName ? (
                     <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                         <input 
                             type="text"
                             value={newName}
                             onChange={(e) => setNewName(e.target.value)}
                             style={{ ...styles.input(themeColors), width: '150px' }}
                             onKeyDown={(e) => { if (e.key === 'Enter') handleNameUpdate(); }}
                         />
                         <button 
                             style={{...styles.actionButton(themeColors), backgroundColor: themeColors.primary, color: themeColors.textSecondary }}
                             onClick={handleNameUpdate}
                         >
                             <MdEdit size={12} />
                         </button>
                         <button 
                             style={{...styles.actionButton(themeColors), backgroundColor: themeColors.error, color: themeColors.textSecondary }}
                             onClick={() => { setNewName(user.name); setIsEditingName(false); }}
                         >
                             <MdClose size={12} />
                         </button>
                     </div>
                 ) : (
                    <span style={styles.userName}>
                         {user.name} 
                         {isMainUser && <span style={styles.mainUserTag}> (Principal)</span>}
                         {isSelf && <span style={styles.mainUserTag}> (Vous)</span>}
                         {isCurrentUserAdmin && !isSelf && (
                             <MdEdit 
                                 size={14} 
                                 style={{ marginLeft: '5px', cursor: 'pointer', color: themeColors.secondary }}
                                 onClick={() => setIsEditingName(true)}
                             />
                         )}
                    </span>
                 )}
                
                <span style={styles.userEmail}>{user.email}</span>
            </div>

            {/* Colonne 2 : Rôle et Édition */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
                {isEditingRole ? (
                    <select
                        style={{ ...styles.input(themeColors), width: '150px' }}
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                    >
                        {roles.map(r => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                    </select>
                ) : (
                    <span style={styles.userRole(themeColors)}>{user.role}</span>
                )}

                {isCurrentUserAdmin && !isMainUser && !isPending && (
                    isEditingRole ? (
                        <div style={{ display: 'flex', gap: '5px' }}>
                             <button 
                                 style={{...styles.actionButton(themeColors), backgroundColor: themeColors.success }}
                                 onClick={handleRoleUpdate}
                             >
                                 <MdEdit size={16} />
                             </button>
                             <button 
                                 style={{...styles.actionButton(themeColors), backgroundColor: themeColors.error }}
                                 onClick={() => setIsEditingRole(false)}
                             >
                                 <MdClose size={16} />
                             </button>
                        </div>
                    ) : (
                        <button 
                            style={styles.actionButton(themeColors)}
                            onClick={() => setIsEditingRole(true)}
                        >
                            <MdEdit size={16} />
                        </button>
                    )
                )}
            </div>

            {/* Colonne 3 : Statut et Bouton de Gestion des Permissions */}
            <div style={{ flex: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '10px' }}>
                {isPending ? (
                    <span style={styles.statusPending(themeColors)}>
                        Invitation en attente (Expire le {new Date(user.invitation_expires).toLocaleDateString()})
                    </span>
                ) : (
                    <span style={styles.statusActive}>
                        {user.is_verified ? 'Actif' : 'Inactif'}
                    </span>
                )}
                
                {/* Bouton de Gestion des Permissions */}
                {isCurrentUserAdmin && !isMainUser && !isPending && (
                    <button 
                         style={{...styles.actionButton(themeColors), backgroundColor: themeColors.defaultButton, color: themeColors.textSecondary }}
                         onClick={() => {
                             setIsManagingPermissions(prev => !prev);
                             if (!isManagingPermissions) {
                                 fetchUserPermissionsAndResources(); // Charger à l'ouverture
                             }
                         }}
                         title="Gérer les permissions"
                    >
                         <MdOutlineReadMore size={16} style={{ marginRight: '5px' }}/>
                         {isManagingPermissions ? 'Fermer la Gestion' : 'Gérer les Accès'}
                    </button>
                )}
            </div>

            {/* Colonne 4 : Actions */}
            <div style={{ flex: 0.5, textAlign: 'right' }}>
                {isCurrentUserAdmin && !isMainUser && !isSelf && (
                    <button 
                        style={{...styles.actionButton(themeColors), backgroundColor: themeColors.error }}
                        onClick={() => onRemove(user.id, user.name)}
                        title="Retirer du compte"
                    >
                        <MdOutlineClear size={16} />
                    </button>
                )}
            </div>
            
            {/* ⭐ Zone de Gestion des Permissions (Modale/Drop-down) */}
            {isManagingPermissions && isCurrentUserAdmin && !isMainUser && (
                <div style={styles.permissionManagerContainer(themeColors)}>
                    {permissionLoading ? (
                        <p style={{ color: themeColors.textSecondary }}>Chargement des ressources et permissions...</p>
                    ) : (
                        <>
                            <h5 style={{ color: themeColors.primary, marginTop: 0 }}>Ressources partagées avec {user.name}</h5>

                            <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '10px' }}>
                                <div style={{ width: '150px' }}>
                                    <label style={styles.label}>Niveau d'Accès :</label>
                                    <select 
                                        style={{ ...styles.input(themeColors), width: '100%', marginTop: '5px' }}
                                        value={accessLevel}
                                        onChange={(e) => setAccessLevel(e.target.value)}
                                        required
                                        disabled={!isEditingPermissions}
                                    >
                                        <option value="read_only">Lecture Seule</option>
                                        <option value="read_write">Lecture & Écriture</option>
                                    </select>
                                </div>
                                
                                <button
                                    type="button"
                                    onClick={() => setIsEditingPermissions(prev => !prev)}
                                    style={{ 
                                        ...styles.saveButton(themeColors), 
                                        backgroundColor: isEditingPermissions ? themeColors.warning : themeColors.primary,
                                        alignSelf: 'flex-end'
                                    }}
                                >
                                    {isEditingPermissions ? 'Annuler l\'Édition' : 'Modifier les Permissions'}
                                </button>
                                
                                {isEditingPermissions && (
                                    <button
                                        type="button"
                                        onClick={handleSavePermissions}
                                        style={{ 
                                            ...styles.saveButton(themeColors), 
                                            backgroundColor: themeColors.success,
                                            alignSelf: 'flex-end'
                                        }}
                                        disabled={permissionLoading}
                                    >
                                        Enregistrer les Permissions
                                    </button>
                                )}
                            </div>

                            <ResourceTreeSelector
                                themeColors={themeColors}
                                resourcesTree={resourcesTree}
                                selectedResourceIds={selectedResourceIds}
                                togglePersonSelection={togglePersonSelectionForUpdate}
                                toggleAccountSelection={toggleAccountSelectionForUpdate}
                                isDisabled={!isEditingPermissions}
                            />
                        </>
                    )}
                </div>
            )}
            
        </div>
    );
};

// 3. Gestion des dossiers partagés (Dossiers partagés)
const ShareManagement = ({ themeColors, notify }) => {
    
    // ⭐ NOUVEAU: Récupérer les permissions que l'utilisateur a reçues.
    // Cette partie nécessiterait l'implémentation de la route GET /permissions/received/:userId
    
    // Simule la récupération des permissions reçues (pour l'affichage)
    const [receivedPermissions, setReceivedPermissions] = useState([
        { sharerName: 'Utilisateur Principal', personName: 'Compte Pro', accountName: 'Compte Courant Pro', accessLevel: 'read_write' },
        { sharerName: 'Jean-Michel', personName: 'Compte Personnel', accountName: 'Livret A', accessLevel: 'read_only' },
    ]);
    
    const handleRemoveShare = (sharerName, resourceName) => {
         if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'accès à ${resourceName} partagé par ${sharerName} ?`)) {
             return;
         }
         // Logique de suppression d'accès (Backend à implémenter)
         notify(`Accès à ${resourceName} supprimé (Simulation).`, 'success');
         // Mise à jour de l'état (simulation)
         setReceivedPermissions(prev => prev.filter(p => p.sharerName !== sharerName || p.resourceName !== resourceName));
    };

    return (
        <div style={styles.settingsContent}>
            <h3 style={{ color: themeColors.text }}>Dossiers partagés (Accès reçu)</h3>
            <p style={{ color: themeColors.text }}>
                Ceci est la liste des personnes et comptes que d'autres utilisateurs vous ont partagés. Vous pouvez choisir de retirer cet accès à tout moment.
            </p>
            
            <div style={styles.userListContainer(themeColors)}>
                {receivedPermissions.map((perm, index) => (
                    <div key={index} style={styles.shareRow(themeColors)}>
                        <div style={{ flex: 2 }}>
                            <span style={styles.userName}>{perm.personName} / {perm.accountName}</span>
                            <span style={styles.userEmail}>Partagé par: {perm.sharerName}</span>
                        </div>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                            <span style={styles.userRole(themeColors)}>
                                {perm.accessLevel === 'read_write' ? 'Lecture/Écriture' : 'Lecture Seule'}
                            </span>
                        </div>
                        <div style={{ flex: 0.5, textAlign: 'right' }}>
                            <button 
                                style={{...styles.actionButton(themeColors), backgroundColor: themeColors.error }}
                                onClick={() => handleRemoveShare(perm.sharerName, perm.accountName)}
                                title="Supprimer l'accès"
                            >
                                <MdDeleteForever size={16} />
                            </button>
                        </div>
                    </div>
                ))}
                {receivedPermissions.length === 0 && (
                     <p style={{ color: themeColors.textSecondary, textAlign: 'center', margin: 0 }}>
                        Aucun dossier partagé pour le moment.
                     </p>
                )}
            </div>
        </div>
    );
};

// 4. Gestion des thèmes (Préférences d'Affichage)
const ThemePreferences = ({ currentTheme, toggleTheme, themeColors }) => {
    // ... (inchangée)
    return (
         <div style={styles.settingsContent}>
             <h3 style={{ color: themeColors.text }}>Thème et Apparence</h3>
             <p style={{ color: themeColors.text }}>
                 Sélectionnez votre thème préféré en visualisant l'arrière-plan associé.
             </p>
             
             <div style={styles.themeGalleryContainer}>
                 {THEME_GALLERY.map(theme => (
                     <div key={theme.value} style={styles.themeCard(themeColors, currentTheme === theme.value)}>
                         <h4 style={{ color: themeColors.text, margin: '5px 0' }}>{theme.label}</h4>
                         
                         <div style={styles.imageWrapper}>
                             <img 
                                 src={theme.image} 
                                 alt={`Aperçu du thème ${theme.label}`} 
                                 style={styles.imagePreview} 
                             />
                         </div>
                         
                         <label style={styles.radioLabel(themeColors)}>
                             <input
                                 type="radio"
                                 name="selectedTheme"
                                 value={theme.value}
                                 checked={currentTheme === theme.value}
                                 onChange={(e) => toggleTheme(e.target.value)}
                                 style={styles.radioInput}
                             />
                             Sélectionner
                         </label>
                     </div>
                 ))}
             </div>
         </div>
    );
};

// 5. Gestion des personnes supprimées (Corbeille des Personnes)
const PersonCorbeille = ({ themeColors, notify }) => {
    // ... (inchangée)
    return (
         <div style={styles.settingsContent}>
             <h3 style={{ color: themeColors.text }}>Corbeille des personnes gérées</h3>
             <p style={{ color: themeColors.text }}>
                 Ici, vous pouvez récupérer les personnes que vous avez masquées ou les supprimer définitivement pour effacer leurs données.
             </p>
             <div style={{ border: `1px solid ${themeColors.inputBorder}`, padding: '15px', borderRadius: '4px' }}>
                  <p style={{ color: themeColors.textSecondary }}>La liste des personnes supprimées sera affichée ici.</p>
                  <p style={{ color: themeColors.textSecondary }}>Restauration et suppression définitive des données associées seront gérées ici.</p>
             </div>
         </div>
    );
};


// ===============================================
// 🚀 COMPOSANT PRINCIPAL : SettingsPage
// ===============================================

export default function SettingsPage({ currentUser, updateCurrentUser }) {
    const navigate = useNavigate(); 
    const { currentTheme, toggleTheme } = useTheme();
    const { notify } = useNotification();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

    const handleGoBack = () => navigate('/dashboard');

    // 💡 FILTRAGE DYNAMIQUE DES ONGLETS
    const allTabs = [
        { id: 'account', label: 'Mon Compte', icon: <MdLock size={20} />, component: UserDataSettings, devOnly: false },
        { id: 'users', label: 'Gestion des Utilisateurs', icon: <MdGroup size={20} />, component: UserManagement, devOnly: true },
        { id: 'share', label: 'Dossiers partagés', icon: <MdAddLink size={20} />, component: ShareManagement, devOnly: true },
        { id: 'preferences', label: 'Préférences d\'Affichage', icon: <MdColorLens size={20} />, component: ThemePreferences, devOnly: false },
        { id: 'corbeille', label: 'Corbeille des Personnes', icon: <MdDeleteForever size={20} />, component: PersonCorbeille, devOnly: true },
    ];

    // On ne garde les onglets "devOnly" que si on est sur localhost
    const visibleTabs = allTabs.filter(tab => !tab.devOnly || isLocalhost);
    
    const [activeTab, setActiveTab] = useState(visibleTabs[0].id);
    const ActiveComponent = visibleTabs.find(t => t.id === activeTab).component;

    return (
        <div style={styles.container(themeColors)}>
            {/* Header */}
            <div style={styles.headerContainer}>
                <h2 style={{ color: themeColors.secondary, margin: '0' }}>Paramètres</h2>
                <button onClick={handleGoBack} style={styles.backButton(themeColors)}>
                    <MdArrowBack size={20} style={{ marginRight: '5px' }} /> Retour
                </button>
            </div>
            
            {/* Navigation Tabs filtrée */}
            <div style={styles.tabContainer}>
                {visibleTabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        style={styles.tabButton(themeColors, activeTab === tab.id)}
                    >
                        {tab.icon} {tab.label} {tab.devOnly && "🛠️"}
                    </button>
                ))}
            </div>

            <div style={styles.contentArea(themeColors)}>
                <ActiveComponent 
                    currentUser={currentUser}
                    updateCurrentUser={updateCurrentUser}
                    themeColors={themeColors}
                    notify={notify}
                    toggleTheme={toggleTheme}
                    currentTheme={currentTheme}
                />
            </div>
        </div>
    );
}

// ===============================================
// 🎨 STYLES DE LA PAGE DE PARAMÈTRES
// ===============================================

const styles = {
    container: (colors) => ({
        padding: '20px 40px',
        maxWidth: '1200px',
        margin: '20px auto',
        backgroundColor: colors.mainBackground,
        minHeight: '80vh',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        color: colors.text
    }),
    headerContainer: { 
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: '1px solid #ccc'
    },
    backButton: (colors) => ({ 
        padding: '8px 15px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: colors.defaultButton,
        color: colors.textSecondary,
        cursor: 'pointer',
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        transition: 'background-color 0.2s'
    }),
    tabContainer: {
        display: 'flex',
        marginBottom: '20px',
        gap: '10px'
    },
    tabButton: (colors, isActive) => ({
        padding: '10px 15px',
        border: 'none',
        borderBottom: isActive ? `3px solid ${colors.primary}` : '3px solid transparent',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        fontWeight: isActive ? 'bold' : 'normal',
        color: isActive ? colors.primary : colors.text,
        fontSize: '1em',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'color 0.2s, border-bottom 0.2s'
    }),
    contentArea: (colors) => ({
        padding: '20px',
        backgroundColor: colors.formBackground,
        borderRadius: '8px',
        border: `1px solid ${colors.inputBorder}`
    }),
    settingsContent: {
        minHeight: '300px'
    },
    form: {
        display: 'grid',
        gridTemplateColumns: '150px 1fr',
        gap: '15px',
        marginTop: '20px',
        maxWidth: '600px'
    },
    label: {
        fontWeight: 'bold',
        alignSelf: 'center',
        color: 'inherit'
    },
    input: (colors) => ({
        padding: '10px',
        borderRadius: '4px',
        border: `1px solid ${colors.inputBorder}`,
        backgroundColor: colors.inputBackground,
        color: colors.text,
        boxSizing: 'border-box'
    }),
    saveButton: (colors) => ({
        padding: '10px 15px',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: colors.success,
        color: colors.textSecondary,
        cursor: 'pointer',
        fontWeight: 'bold'
    }),
    userListContainer: (colors) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        marginTop: '15px',
        padding: '10px',
        border: `1px solid ${colors.inputBorder}`,
        borderRadius: '4px',
    }),
    userRow: (colors, isMainUser) => ({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px',
        backgroundColor: isMainUser ? colors.formBackground : colors.mainBackground,
        border: isMainUser ? `1px solid ${colors.secondary}` : `1px solid ${colors.inputBorder}`,
        borderRadius: '4px',
        transition: 'background-color 0.2s',
        // ⭐ AJOUT: Permettre au manager de permission de prendre toute la largeur
        flexWrap: 'wrap',
    }),
    shareRow: (colors) => ({
         display: 'flex',
         justifyContent: 'space-between',
         alignItems: 'center',
         padding: '10px',
         backgroundColor: colors.mainBackground,
         border: `1px solid ${colors.inputBorder}`,
         borderRadius: '4px',
         transition: 'background-color 0.2s',
    }),
    userName: {
        fontWeight: 'bold',
        fontSize: '1.1em',
        color: 'inherit',
    },
    userEmail: {
        fontSize: '0.9em',
        color: 'gray',
    },
    mainUserTag: {
        fontSize: '0.7em',
        fontWeight: 'normal',
        backgroundColor: '#FFEB3B',
        color: '#333',
        padding: '2px 5px',
        borderRadius: '3px',
        marginLeft: '5px',
        verticalAlign: 'top',
    },
    userRole: (colors) => ({
        fontWeight: 'bold',
        color: colors.text,
        padding: '5px 10px',
        borderRadius: '4px',
        backgroundColor: colors.defaultButton,
    }),
    actionButton: (colors) => ({
        padding: '5px',
        border: `1px solid ${colors.inputBorder}`,
        borderRadius: '4px',
        backgroundColor: colors.formBackground,
        color: colors.text,
        cursor: 'pointer',
        transition: 'background-color 0.2s',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    }),
    statusActive: {
        color: 'green',
        fontWeight: 'bold',
    },
    statusPending: (colors) => ({
        color: colors.warning,
        fontStyle: 'italic',
    }),
    themeGalleryContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        marginTop: '20px',
        justifyContent: 'flex-start'
    },
    themeCard: (colors, isSelected) => ({
        padding: '10px',
        border: isSelected ? `3px solid ${colors.primary}` : `1px solid ${colors.inputBorder}`,
        borderRadius: '8px',
        width: '180px',
        textAlign: 'center',
        backgroundColor: colors.mainBackground,
        boxShadow: isSelected ? '0 0 10px rgba(0, 123, 255, 0.5)' : 'none',
        transition: 'border 0.2s, box-shadow 0.2s'
    }),
    imageWrapper: {
        height: '100px',
        width: '100%',
        overflow: 'hidden',
        borderRadius: '4px',
        marginBottom: '10px',
    },
    imagePreview: {
        width: '100%',
        height: 'auto',
        minHeight: '100px',
        display: 'block',
        objectFit: 'cover'
    },
    radioLabel: (colors) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '5px',
        cursor: 'pointer',
        fontWeight: 'bold',
        color: colors.text,
        marginTop: '5px'
    }),
    radioInput: {
        cursor: 'pointer',
        transform: 'scale(1.2)'
    },
    treeSelectorContainer: (colors) => ({
        border: `1px solid ${colors.inputBorder}`,
        borderRadius: '4px',
        backgroundColor: colors.inputBackground,
        padding: '10px',
        marginTop: '5px',
        maxHeight: '300px',
        overflowY: 'auto',
    }),
    treePersonRow: (colors) => ({
        margin: '5px 0',
        borderBottom: `1px solid ${colors.inputBorder}`,
        paddingBottom: '5px',
    }),
    treeRowHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '3px 0',
        fontWeight: 'bold'
    },
    treeChildren: {
        marginLeft: '25px',
        borderLeft: '2px solid #ccc',
        paddingLeft: '10px',
        marginTop: '5px',
        fontSize: '0.9em',
    },
    treeAccountRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '2px 0'
    },
    badge: {
        fontSize: '0.7em',
        color: 'gray',
        backgroundColor: '#eee',
        padding: '2px 5px',
        borderRadius: '3px',
    },
    permissionManagerContainer: (colors) => ({
         width: '100%',
         padding: '15px',
         marginTop: '10px',
         backgroundColor: colors.inputBackground,
         borderRadius: '4px',
         borderTop: `2px solid ${colors.secondary}`,
    }),
    securityCard : (colors) => ({
        backgroundColor: colors.mainBackground, 
        padding: '15px', 
        borderRadius: '8px', 
        border: `1px solid ${colors.inputBorder}`,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px'
    }),
};